import { api } from "@/lib/api/client";
import type { GuideStatus } from "@nodus/ui";

/** One guide in `/guides/me/` (#436) — the member's own view of it. */
export interface GuideSummary {
  /** `g:<id>` for an operator guide, `b:<key>` for a built-in one. */
  key: string;
  kind: "operator" | "builtin";
  title: string;
  /** First line of the guide's intro, plain text. */
  summary: string;
  icon: string;
  /** Portal areas the guide is placed in (keys of `MyGuides.areas`). */
  areas: string[];
  audience: "all" | "admins";
  status: GuideStatus;
  done: number;
  total: number;
  /** Whether the one-time area banner should still nudge (MR 6 renders it). */
  banner_pending: boolean;
  /** Whether the guide may appear in the popup of unfinished guides at login. */
  popup: boolean;
}

export interface MyGuides {
  /** Area key → portal route prefix. */
  areas: Record<string, string>;
  guides: GuideSummary[];
  is_company_admin: boolean;
}

export interface GuideAttachment {
  id: number;
  filename: string;
  description: string;
  file_size: number;
  url: string;
}

/** An FAQ article a step shows live. */
export interface GuideRef {
  id: number;
  title: string;
  category: string;
  content: string;
  attachments: GuideAttachment[];
}

export type GuideStepBehaviour = "info" | "action" | "auto";

export interface GuideStep {
  /** A built-in step key, or `s:<id>` for a step the office wrote. */
  key: string;
  title: string;
  content_kind: "markdown" | "faq_ref" | "block";
  body: string;
  ref: GuideRef | null;
  block_key: string;
  /** The block's resolved config; `null` when the step has no block. */
  block: Record<string, unknown> | null;
  /** Built-in steps' header icon, a lucide name (`knownIcon`); "" for none. */
  icon?: string;
  /** A picture key (`components/guides/illustrations`); "" for none. */
  illustration?: string;
  /** Markdown for an orange warning callout under the text; "" for none. */
  callout?: string;
  behaviour: GuideStepBehaviour;
  /** A teaching block (booking a room): in the guide its last action is
   *  practice — no API call, the step is ticked and the guide moves on. */
  practice?: boolean;
  /** `admins` = a company step, shared by the company's admins. */
  audience: "all" | "admins";
  important: boolean;
  done: boolean;
  /** Who ticked a company step; `null` when unknown. */
  done_by: string | null;
}

export interface GuideDetail {
  key: string;
  kind: "operator" | "builtin";
  title: string;
  /** Markdown intro. */
  intro: string;
  icon: string;
  areas: string[];
  status: GuideStatus;
  done: number;
  total: number;
  steps: GuideStep[];
}

export type GuideStateAction = "seen" | "dismiss" | "reopen";

/** A quiz answer that isn't right yet (400 from the quiz endpoint). */
export interface QuizWrong {
  key: string;
  /** Where to look again — never the answer. */
  hint: string;
}

export interface QuizRefused {
  detail: string;
  wrong: QuizWrong[];
}

export interface ColleagueGuideCell {
  done: number;
  total: number;
  completed: boolean;
}

export interface ColleaguesGuideProgress {
  guides: { key: string; title: string; company: { done: number; total: number } }[];
  members: {
    name: string;
    is_admin: boolean;
    cells: Record<string, ColleagueGuideCell>;
  }[];
}

/** Every guide query shares this prefix, so one invalidation refreshes them all. */
export const GUIDES_KEY = ["guides"] as const;
export const guideQueryKey = (key: string) => ["guides", "detail", key] as const;
export const COLLEAGUES_GUIDES_KEY = ["guides", "colleagues"] as const;

const enc = encodeURIComponent;

export const guidesApi = {
  list: () => api.get<MyGuides>("/guides/me/"),
  get: (key: string) => api.get<GuideDetail>(`/guides/me/${enc(key)}/`),
  /** Own steps and company steps have separate endpoints; `company` picks one. */
  markStep: (key: string, stepKey: string, done: boolean, company = false) =>
    api.post<GuideDetail>(`/guides/me/${enc(key)}/${company ? "company-steps" : "steps"}/${enc(stepKey)}/`, { done }),
  /** Answer a quiz step: question key → option id. 200 = passed (the step is
   *  done), 400 `QuizRefused` = some answers are not right yet. */
  answerQuiz: (key: string, stepKey: string, answers: Record<string, string>) =>
    api.post<GuideDetail>(`/guides/me/${enc(key)}/steps/${enc(stepKey)}/quiz/`, { answers }),
  setState: (key: string, action: GuideStateAction) =>
    api.post<GuideDetail>(`/guides/me/${enc(key)}/state/`, { action }),
  dismissBanner: (area: string) => api.post<{ dismissed: string[] }>("/guides/me/banner-dismiss/", { area }),
  colleagues: () => api.get<ColleaguesGuideProgress>("/guides/me/colleagues/"),
};
